import React, { useState, type MouseEvent } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { KeenIcon } from '@/components';
import { enqueueSnackbar } from 'notistack';

interface IPasswordProps {
  title: string;
}

const initialValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
};

const passwordSchema = Yup.object().shape({
  currentPassword: Yup.string()
    .min(6, 'Mindestens 6 Zeichen')
    .max(100, 'Maximal 100 Zeichen')
    .required('Aktuelles Passwort ist erforderlich'),
  newPassword: Yup.string()
    .min(6, 'Mindestens 6 Zeichen')
    .max(100, 'Maximal 100 Zeichen')
    .required('Neues Passwort ist erforderlich'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwörter müssen übereinstimmen')
    .required('Bitte bestätigen Sie das neue Passwort')
});

const extractApiError = (error: any): string => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (Array.isArray(detail)) {
    return detail.map((e) => e.msg).join(' ');
  }

  return 'Ein unerwarteter Fehler ist aufgetreten.';
};

const Password = ({ title }: IPasswordProps) => {
  const [submitLoading, setSubmitLoading] = useState(false);

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const API_URL = import.meta.env.VITE_APP_API_URL;
  const API_PASSWORD = `${API_URL}/password`;

  const toggle =
    (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
    (event: MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      setter((prev) => !prev);
    };

  const formik = useFormik({
    initialValues,
    validationSchema: passwordSchema,
    onSubmit: async (values, { setStatus, setSubmitting, resetForm }) => {
      setSubmitLoading(true);

      try {
        await axios.put(API_PASSWORD, {
          old_password: values.currentPassword,
          new_password: values.newPassword,
          new_password_confirmation: values.confirmPassword
        });

        enqueueSnackbar('Passwort wurde erfolgreich geändert.', {
          variant: 'success'
        });

        resetForm();
      } catch (error: any) {
        const errorMessage = extractApiError(error);
        setStatus(errorMessage);
        enqueueSnackbar(errorMessage, { variant: 'error' });
      } finally {
        setSubmitting(false);
        setSubmitLoading(false);
      }
    }
  });

  return (
    <div className="card pb-2.5">
      <form onSubmit={formik.handleSubmit}>
        <div className="card-header" id="password_settings">
          <h3 className="card-title">{title || 'Passwort ändern'}</h3>
        </div>

        <div className="card-body grid gap-5">
          {/* Aktuelles Passwort */}
          <div className="grid gap-1">
            <label className="form-label">Aktuelles Passwort</label>
            <label className="input">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                placeholder="Aktuelles Passwort"
                {...formik.getFieldProps('currentPassword')}
                className={clsx('form-control', {
                  'is-invalid': formik.touched.currentPassword && formik.errors.currentPassword
                })}
              />
              <button className="btn btn-icon" onClick={toggle(setShowCurrentPassword)}>
                <KeenIcon
                  icon="eye"
                  className={clsx('text-gray-500', { hidden: showCurrentPassword })}
                />
                <KeenIcon
                  icon="eye-slash"
                  className={clsx('text-gray-500', { hidden: !showCurrentPassword })}
                />
              </button>
            </label>
            {formik.touched.currentPassword && formik.errors.currentPassword && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.currentPassword}
              </span>
            )}
          </div>

          {/* Neues Passwort */}
          <div className="grid gap-1">
            <label className="form-label">Neues Passwort</label>
            <label className="input">
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Neues Passwort"
                {...formik.getFieldProps('newPassword')}
                className={clsx('form-control', {
                  'is-invalid': formik.touched.newPassword && formik.errors.newPassword
                })}
              />
              <button className="btn btn-icon" onClick={toggle(setShowNewPassword)}>
                <KeenIcon
                  icon="eye"
                  className={clsx('text-gray-500', { hidden: showNewPassword })}
                />
                <KeenIcon
                  icon="eye-slash"
                  className={clsx('text-gray-500', { hidden: !showNewPassword })}
                />
              </button>
            </label>
            {formik.touched.newPassword && formik.errors.newPassword && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.newPassword}
              </span>
            )}
          </div>

          {/* Neues Passwort bestätigen */}
          <div className="grid gap-1">
            <label className="form-label">Neues Passwort bestätigen</label>
            <label className="input">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Neues Passwort bestätigen"
                {...formik.getFieldProps('confirmPassword')}
                className={clsx('form-control', {
                  'is-invalid': formik.touched.confirmPassword && formik.errors.confirmPassword
                })}
              />
              <button className="btn btn-icon" onClick={toggle(setShowConfirmPassword)}>
                <KeenIcon
                  icon="eye"
                  className={clsx('text-gray-500', { hidden: showConfirmPassword })}
                />
                <KeenIcon
                  icon="eye-slash"
                  className={clsx('text-gray-500', { hidden: !showConfirmPassword })}
                />
              </button>
            </label>
            {formik.touched.confirmPassword && formik.errors.confirmPassword && (
              <span role="alert" className="text-danger text-xs">
                {formik.errors.confirmPassword}
              </span>
            )}
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitLoading || formik.isSubmitting}
            >
              {submitLoading ? 'Bitte warten …' : 'Passwort ändern'}
            </button>
          </div>

          {formik.status && (
            <div className="text-danger text-xs" role="alert">
              {formik.status}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export { Password };
